import { useState } from "react";
import { Card, Group, Select, Text } from "@mantine/core";
import { transactionsApi } from "../../api/transactions";
import type { Transaction } from "../../types/domain";
import { usePaginatedList } from "../../hooks/usePaginatedList";
import { PageHeader } from "../../components/common/PageHeader";
import { DataTable, type Column } from "../../components/common/DataTable";
import { StatusBadge } from "../../components/common/StatusBadge";
import { TripLink } from "../../components/common/TripLink";
import {
  DetailDrawer,
  DetailField,
  DetailSection,
} from "../../components/common/DetailDrawer";
import { ExportMenu } from "../../components/common/ExportMenu";
import { collectAll, type ExportField } from "../../lib/export";
import { formatMoney, formatDateTime, en } from "../../lib/format";
import { humanize, toSelectOptions } from "../../lib/labels";
import { errorMessage } from "../../lib/axios";

const STATUS_OPTIONS = toSelectOptions(
  ["SUCCESS", "PENDING", "FAILED"],
  "All statuses",
);
// The backend `transactionType` enum is DEBIT | CREDIT. This filter previously
// listed category-ish values ("TRIP_PAYMENT", "DRIVER_PAYOUT", …) that matched
// nothing server-side, so it silently returned everything.
const TYPE_OPTIONS = toSelectOptions(["DEBIT", "CREDIT"], "All types");

// Backend TransactionCategory values (types/transaction.ts).
const CATEGORY_OPTIONS = toSelectOptions(
  ["trip", "walletTopUp", "debtPayment", "manualPayout"],
  "All categories",
);

function reasonText(r: Transaction["reason"]): string | undefined {
  if (!r) return undefined;
  return typeof r === "string" ? r : en(r);
}

const txnExportFields: ExportField<Transaction>[] = [
  { label: "Type", value: (t) => t.transactionType },
  { label: "Amount", value: (t) => t.amount },
  { label: "Mode", value: (t) => t.paymentMode },
  { label: "Category", value: (t) => t.category },
  { label: "Status", value: (t) => t.status },
  { label: "Reason", value: (t) => reasonText(t.reason) },
  { label: "Date", value: (t) => t.createdAt },
  { label: "User", value: (t) => t.userID },
  { label: "Driver", value: (t) => t.driverID },
];

export function TransactionsPage() {
  const [status, setStatus] = useState("");
  const [transactionType, setTransactionType] = useState("");
  const [category, setCategory] = useState("");
  const [selected, setSelected] = useState<Transaction | null>(null);

  const { page, setPage, result, isLoading, error } =
    usePaginatedList<Transaction>({
      queryKey: ["transactions"],
      fetcher: transactionsApi.list,
      filters: { status, transactionType, category },
    });

  const columns: Column<Transaction>[] = [
    {
      key: "type",
      header: "Type",
      render: (t) => (
        <Text size="sm" fw={500}>
          {humanize(t.transactionType)}
        </Text>
      ),
    },
    {
      key: "amount",
      header: "Amount",
      align: "right",
      render: (t) => formatMoney(t.amount),
    },
    { key: "mode", header: "Mode", render: (t) => humanize(t.paymentMode) },
    {
      key: "reason",
      header: "Reason",
      render: (t) => (
        <Text size="sm" lineClamp={1}>
          {reasonText(t.reason) ?? "—"}
        </Text>
      ),
    },
    {
      key: "status",
      header: "Status",
      render: (t) => <StatusBadge status={t.status} />,
    },
    {
      key: "created",
      header: "Date",
      render: (t) => (
        <Text size="xs" c="dimmed">
          {formatDateTime(t.createdAt)}
        </Text>
      ),
    },
  ];

  return (
    <>
      <PageHeader title="Transactions" subtitle="Payments, payouts & refunds" />
      <Card withBorder radius="md" padding="lg">
        <Group mb="md" gap="sm">
          <Select
            data={STATUS_OPTIONS}
            value={status}
            onChange={(v) => {
              setStatus(v ?? "");
              setPage(1);
            }}
            w={160}
            allowDeselect={false}
          />
          <Select
            data={TYPE_OPTIONS}
            value={transactionType}
            onChange={(v) => {
              setTransactionType(v ?? "");
              setPage(1);
            }}
            w={140}
            allowDeselect={false}
          />
          <Select
            data={CATEGORY_OPTIONS}
            value={category}
            onChange={(v) => {
              setCategory(v ?? "");
              setPage(1);
            }}
            w={180}
            allowDeselect={false}
          />
          <ExportMenu
            filename="transactions"
            fields={txnExportFields}
            fetchAll={() =>
              collectAll(transactionsApi.list, {
                status: status || undefined,
                transactionType: transactionType || undefined,
                category: category || undefined,
              })
            }
          />
        </Group>
        <DataTable
          columns={columns}
          rows={result?.items ?? []}
          rowKey={(t) => t._id}
          loading={isLoading}
          error={error ? errorMessage(error) : null}
          emptyLabel="No transactions found"
          onRowClick={setSelected}
          page={page}
          totalPages={result?.totalPages}
          total={result?.total}
          onPageChange={setPage}
        />
      </Card>

      <DetailDrawer
        opened={!!selected}
        onClose={() => setSelected(null)}
        title="Transaction"
      >
        {selected && (
          <>
            <DetailSection title="Transaction">
              <DetailField label="Type" value={humanize(selected.transactionType)} />
              <DetailField label="Amount" value={formatMoney(selected.amount)} />
              <DetailField label="Mode" value={humanize(selected.paymentMode)} />
              <DetailField label="Category" value={humanize(selected.category)} />
              <DetailField label="Status" value={<StatusBadge status={selected.status} />} />
              <DetailField label="Date" value={formatDateTime(selected.createdAt)} />
            </DetailSection>
            <DetailSection title="Context">
              <DetailField label="Reason" value={reasonText(selected.reason)} />
              <DetailField label="Remarks" value={selected.remarks} />
              <DetailField label="Trip" value={<TripLink trip={selected.tripID} />} />
              <DetailField label="User ID" value={selected.userID} />
              <DetailField label="Driver ID" value={selected.driverID} />
            </DetailSection>
          </>
        )}
      </DetailDrawer>
    </>
  );
}
